
import React, { useState, useEffect } from 'react';
import { View } from '../App';

import { 
    HomeIcon, 
    CameraIcon, 
    CalendarIcon, 
    FolderIcon, 
    SettingsIcon, 
    BriefcaseIcon, 
    DocumentTextIcon, 
    UsersIcon, 
    BadgeIcon,
    XIcon
} from './icons';

interface OnboardingStep {
    title: string;
    description: string;
    icon: React.ReactNode;
}

const steps: Record<View, OnboardingStep> = {
    dashboard: {
        title: "Tu Centro de Operaciones",
        description: "Aquí verás un resumen de los servicios activos hoy. Usa el botón 'Iniciar Nuevo Expediente' para comenzar con un cliente.",
        icon: <HomeIcon />
    },
    newService: {
        title: "Digitalización de Datos",
        description: "Toma fotos al anverso y reverso del DNI. La IA extraerá los datos automáticamente. Si el cliente ya existe, búscalo en la base de datos.",
        icon: <CameraIcon />
    },
    calendar: {
        title: "Gestión de Tiempos",
        description: "Organiza las salas de velatorio y las ceremonias. Haz clic en cualquier día para ver el cronograma detallado de servicios.",
        icon: <CalendarIcon />
    },
    cases: {
        title: "Archivo Histórico",
        description: "Busca expedientes pasados por nombre o ID. Puedes filtrar por estado para ver solo lo que está pendiente de facturar.",
        icon: <FolderIcon />
    },
    settings: {
        title: "Configuración Personal",
        description: "Personaliza tu perfil, los datos de la funeraria y el catálogo de productos. Aquí también puedes desactivar este guía.",
        icon: <SettingsIcon />
    },
    servicesManagement: {
        title: "Catálogo IA",
        description: "Gestiona tus ataúdes y urnas. Usa el botón 'Generar con IA' para obtener fotos profesionales si no tienes imágenes propias.",
        icon: <BriefcaseIcon />
    },
    documentTemplates: {
        title: "Constructor Legal",
        description: "Arrastra 'Campos Inteligentes' como {{deceased.fullName}} al documento. Se rellenarán solos cuando generes un PDF desde un caso real.",
        icon: <DocumentTextIcon />
    },
    clients: {
        title: "Relaciones Familiares",
        description: "Aquí puedes ver el historial de cada familia. El sistema detecta automáticamente quién ha pagado qué servicios anteriormente.",
        icon: <UsersIcon />
    },
    caseDetails: {
        title: "Expediente Detallado",
        description: "Desde aquí generas la documentación final. Una vez firmado todo, usa 'Finalizar y Facturar' para dar por concluido el servicio.",
        icon: <BadgeIcon />
    }
};

export const OnboardingGuide: React.FC<{ activeView: View; onClose: () => void }> = ({ activeView, onClose }) => {
    const [visible, setVisible] = useState(false);
    const step = steps[activeView];

    useEffect(() => {
        setVisible(false);
        const timer = setTimeout(() => setVisible(true), 500);
        return () => clearTimeout(timer);
    }, [activeView]);

    if (!step) return null;

    return (
        <div className={`fixed bottom-24 right-8 z-[100] w-80 transition-all duration-700 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
            <div className="bg-slate-900/95 dark:bg-primary-500 backdrop-blur-xl border border-white/20 rounded-[24px] p-6 shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary-500/10 dark:bg-white/10 rounded-bl-[60px] -mr-8 -mt-8 transition-transform group-hover:scale-125"></div>
                
                <header className="flex justify-between items-start mb-4 relative z-10">
                    <div className="p-2.5 bg-primary-500 text-slate-900 dark:bg-white dark:text-primary-600 rounded-xl shadow-lg w-10 h-10 flex items-center justify-center">
                        <div className="w-6 h-6">
                            {step.icon}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-white/40 hover:text-white dark:text-slate-800/40 dark:hover:text-slate-900 transition-colors">
                        <XIcon className="w-5 h-5" />
                    </button>
                </header>

                <div className="relative z-10">
                    <h4 className="text-white dark:text-slate-900 font-serif font-bold text-lg leading-tight mb-2">{step.title}</h4>
                    <p className="text-white/70 dark:text-slate-900/70 text-xs font-medium leading-relaxed mb-4">{step.description}</p>
                    
                    <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 dark:bg-white animate-pulse"></span>
                        <span className="text-[10px] font-black text-primary-500 dark:text-white uppercase tracking-[0.2em]">Guía Activa</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
